import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiClientError } from '@suangua/shared';

/**
 * 管理端共用件（轻量功能向，视觉与用户端玄墨鎏金呼应但保持高信息密度）：
 * - useAdminGuard：统一错误出口（401 → 跳登录页）
 * - PageHeader / Pagination / StatusBadge / EmptyRow
 */

/** 统一错误处理：401 视为登录失效 → 回登录页；返回展示文案 */
export function useAdminGuard() {
  const navigate = useNavigate();
  return useCallback(
    (err: unknown): string => {
      if (err instanceof ApiClientError && err.status === 401) {
        navigate('/admin/login', { replace: true });
        return '登录已失效，请重新登录';
      }
      return err instanceof ApiClientError ? err.message : '操作失败，请稍后再试';
    },
    [navigate],
  );
}

/** 页头：标题 + 说明 + 右侧操作区 */
export function PageHeader(props: { title: string; desc?: string; children?: React.ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <div>
        <h1 className="text-lg font-bold text-stone-800">{props.title}</h1>
        {props.desc && <p className="mt-0.5 text-xs text-stone-400">{props.desc}</p>}
      </div>
      <div className="ml-auto flex items-center gap-2">{props.children}</div>
    </div>
  );
}

/** 分页条：总数 + 页码 + 上一页/下一页 */
export function Pagination(props: { page: number; size: number; total: number; onPage: (p: number) => void }) {
  const { page, size, total, onPage } = props;
  const pages = Math.max(1, Math.ceil(total / size));
  const canPrev = page > 1;
  const canNext = page < pages;
  return (
    <div className="flex items-center justify-between border-t border-stone-100 px-4 py-3 text-xs text-stone-500">
      <span>
        共 {total} 条 · 第 {Math.min(page, pages)} / {pages} 页
      </span>
      <div className="flex items-center gap-2">
        <button
          disabled={!canPrev}
          onClick={() => onPage(page - 1)}
          className="rounded-lg border border-stone-200 px-3 py-1.5 transition-colors hover:bg-stone-50 disabled:opacity-40"
        >
          上一页
        </button>
        <button
          disabled={!canNext}
          onClick={() => onPage(page + 1)}
          className="rounded-lg border border-stone-200 px-3 py-1.5 transition-colors hover:bg-stone-50 disabled:opacity-40"
        >
          下一页
        </button>
      </div>
    </div>
  );
}

/** 状态徽章 */
export function StatusBadge(props: { tone: 'green' | 'red' | 'amber' | 'gray'; children: React.ReactNode }) {
  const cls = {
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-600',
    amber: 'bg-amber-100 text-amber-700',
    gray: 'bg-stone-100 text-stone-500',
  }[props.tone];
  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {props.children}
    </span>
  );
}

/** 表格容器（白卡 + 圆角 + 分页插槽） */
export function TableCard(props: { children: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <div className="max-w-5xl overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
      <div className="overflow-x-auto">{props.children}</div>
      {props.footer}
    </div>
  );
}

/** 空态行 */
export function EmptyRow(props: { text: string }) {
  return <p className="px-4 py-10 text-center text-sm text-stone-400">{props.text}</p>;
}

/** 加载态 */
export function LoadingRow() {
  return <p className="px-4 py-10 text-center text-sm text-stone-400">加载中…</p>;
}

/** 相对时间（管理端列表用） */
export function useFromNow() {
  return useMemo(
    () =>
      (iso: string): string => {
        const t = new Date(iso).getTime();
        if (Number.isNaN(t)) return '';
        const min = Math.floor((Date.now() - t) / 60000);
        if (min < 1) return '刚刚';
        if (min < 60) return `${min} 分钟前`;
        const hour = Math.floor(min / 60);
        if (hour < 24) return `${hour} 小时前`;
        const day = Math.floor(hour / 24);
        if (day === 1) return '昨天';
        if (day < 7) return `${day} 天前`;
        return new Date(t).toLocaleDateString();
      },
    [],
  );
}

/** 截止时间展示（0006）：远未来（9999-12-31）识别为「永久」 */
export function deadlineText(iso: string | null | undefined): string {
  if (!iso) return '永久';
  return new Date(iso).getTime() >= new Date('9999-12-31T23:59:59.999Z').getTime() - 1
    ? '永久'
    : new Date(iso).toLocaleDateString();
}

/** 处置期限选择结果：days = 天数；permanent = 永久 */
export interface ModerationChoice {
  days?: number;
  permanent?: boolean;
}

/**
 * 通用处置期限弹窗（0006）：预设 1/3/7/30/90 天 + 永久 + 自定义天数（1~3650）。
 * 供用户管理（封禁/禁言/禁发帖/禁回复/禁换头像）与举报处置（禁言/封禁/禁换头像）共用。
 */
export function ModerationModal(props: {
  title: string;
  desc?: string;
  /** 允许选择「永久」（默认 true） */
  allowPermanent?: boolean;
  onConfirm: (choice: ModerationChoice) => void;
  onClose: () => void;
  /** 额外操作区（如「解除禁止」） */
  extra?: React.ReactNode;
}) {
  const [custom, setCustom] = useState('');
  const allowPermanent = props.allowPermanent !== false;
  const customDays = Number(custom);
  const customValid = Number.isInteger(customDays) && customDays >= 1 && customDays <= 3650;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-stone-900/40 p-4" onClick={props.onClose}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-stone-800">{props.title}</h3>
        {props.desc && <p className="mt-1 text-xs text-stone-400">{props.desc}</p>}
        <div className="mt-4 grid grid-cols-3 gap-2">
          {[1, 3, 7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => props.onConfirm({ days: d })}
              className="rounded-lg border border-stone-200 py-2 text-xs text-stone-600 transition-colors hover:border-amber-400 hover:bg-amber-50 hover:text-amber-700"
            >
              {d} 天
            </button>
          ))}
          {allowPermanent && (
            <button
              onClick={() => props.onConfirm({ permanent: true })}
              className="rounded-lg border border-red-200 py-2 text-xs text-red-500 transition-colors hover:bg-red-50"
            >
              永久
            </button>
          )}
        </div>
        {/* 自定义天数 */}
        <div className="mt-3 flex items-center gap-2">
          <input
            value={custom}
            onChange={(e) => setCustom(e.target.value.replace(/[^\d]/g, ''))}
            placeholder="自定义天数"
            inputMode="numeric"
            maxLength={4}
            className="w-28 rounded-lg border border-stone-200 px-2.5 py-1.5 text-xs outline-none focus:border-stone-400"
          />
          <button
            disabled={!customValid}
            onClick={() => props.onConfirm({ days: customDays })}
            className="rounded-lg bg-stone-800 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-stone-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            确定
          </button>
          {custom && !customValid && <span className="text-[11px] text-red-400">1~3650</span>}
        </div>
        {props.extra}
        <button onClick={props.onClose} className="mt-4 w-full rounded-lg border border-stone-200 py-2 text-xs text-stone-500 hover:bg-stone-50">
          取消
        </button>
      </div>
    </div>
  );
}
